import { inject, ref, type App, type InjectionKey, type Ref } from 'vue';
import { io, type Socket } from 'socket.io-client';
import {
  LIVE,
  PERSONAL,
  PROJECT_EVENTS,
  type ProjectEventName,
  type ProjectEvents,
} from './protocol.ts';

export type ProjectHandlers = {
  [E in ProjectEventName]?: (payload: ProjectEvents[E]) => void;
} & {
  /** After a reconnect: events sent meanwhile are gone, so refetch what's shown. */
  resync?: () => void;
  /** Access was lost (removed from the project, or it was deleted). */
  revoked?: () => void;
};

export interface ProjectSubscription {
  /** The project's id, once the server has accepted the subscription. */
  readonly projectId: Ref<string | null>;
  /** Tell others you have `field` of `itemId` open (call again every 10s while open). */
  editing(itemId: string, field: string): void;
  stopEditing(itemId: string, field: string): void;
  close(): void;
}

/** What the app needs from the live connection (tests provide a fake). */
export interface Realtime {
  readonly clientId: string;
  readonly connected: Ref<boolean>;
  subscribeProject(org: string, key: string, handlers: ProjectHandlers): ProjectSubscription;
  /**
   * Hear that your notifications changed, and after a reconnect (events sent
   * meanwhile are gone). Returns a function that stops listening.
   */
  onNotifications(handler: () => void): () => void;
  install(app: App): void;
}

interface Entry {
  org: string;
  key: string;
  handlers: ProjectHandlers;
  projectId: Ref<string | null>;
}

/** A random id for this tab, so it can recognize (and skip) echoes of its own changes. */
function newClientId(): string {
  return globalThis.crypto.randomUUID().replaceAll('-', '');
}

/**
 * The live connection for this tab: one socket, any number of project
 * subscriptions. It connects while someone is signed in, re-subscribes after a
 * reconnect (and asks each view to resync), and drops events this tab caused.
 */
export class RealtimeClient implements Realtime {
  readonly clientId = newClientId();
  /** Whether the socket is connected (the UI can show a quiet "offline" state). */
  readonly connected = ref(false);
  readonly #entries = new Set<Entry>();
  readonly #personal = new Set<() => void>();
  readonly #url: string;
  #socket: Socket | null = null;
  #hasConnected = false;

  constructor({ url = '' }: { url?: string } = {}) {
    this.#url = url;
  }

  connect(): void {
    if (this.#socket) return;
    const socket = io(this.#url, {
      path: '/socket.io',
      transports: ['websocket'],
      withCredentials: true,
      // The server skips this tab when it made the change (matched with our session only).
      auth: { clientId: this.clientId },
    });
    socket.on('connect', () => {
      this.connected.value = true;
      const reconnect = this.#hasConnected;
      this.#hasConnected = true;
      for (const entry of this.#entries) void this.#subscribe(entry, reconnect);
      if (reconnect) for (const handler of this.#personal) handler();
    });
    socket.on('disconnect', () => (this.connected.value = false));
    for (const event of PROJECT_EVENTS)
      socket.on(event, (payload: object) => this.#dispatch(event, payload));
    socket.on(PERSONAL.notifications, () => {
      for (const handler of this.#personal) handler();
    });
    socket.on(LIVE.projectRevoked, ({ projectId }: { projectId: string }) => {
      for (const entry of this.#entries)
        if (entry.projectId.value === projectId) entry.handlers.revoked?.();
    });
    this.#socket = socket;
  }

  disconnect(): void {
    this.#socket?.disconnect();
    this.#socket = null;
    this.#hasConnected = false;
    this.connected.value = false;
    for (const entry of this.#entries) entry.projectId.value = null;
  }

  /** Hear a project's changes until `close()`. Works before connecting (it joins on connect). */
  subscribeProject(org: string, key: string, handlers: ProjectHandlers): ProjectSubscription {
    const entry: Entry = { org, key, handlers, projectId: ref(null) };
    this.#entries.add(entry);
    if (this.#socket?.connected) void this.#subscribe(entry, false);
    const socket = () => this.#socket;
    return {
      projectId: entry.projectId,
      editing: (itemId, field) => {
        if (entry.projectId.value)
          socket()?.emit('editing', { projectId: entry.projectId.value, itemId, field });
      },
      stopEditing: (itemId, field) => {
        if (entry.projectId.value)
          socket()?.emit('editing:stop', { projectId: entry.projectId.value, itemId, field });
      },
      close: () => {
        this.#entries.delete(entry);
        const id = entry.projectId.value;
        // Another view may still be watching the same project.
        const shared = [...this.#entries].some((e) => e.projectId.value === id);
        if (id && !shared) socket()?.emit('unsubscribe', { projectId: id });
      },
    };
  }

  onNotifications(handler: () => void): () => void {
    this.#personal.add(handler);
    return () => this.#personal.delete(handler);
  }

  async #subscribe(entry: Entry, reconnect: boolean): Promise<void> {
    const socket = this.#socket;
    if (!socket) return;
    try {
      const reply = (await socket
        .timeout(10_000)
        .emitWithAck('subscribe', { org: entry.org, key: entry.key })) as {
        ok: boolean;
        projectId?: string;
      };
      if (!this.#entries.has(entry)) return;
      entry.projectId.value = reply.ok ? (reply.projectId ?? null) : null;
      if (reply.ok && reconnect) entry.handlers.resync?.();
    } catch {
      // No answer (e.g. the connection dropped again): the next connect retries.
    }
  }

  #dispatch(event: ProjectEventName, payload: object): void {
    // A tab can be in several project rooms: each entry takes its own project's events.
    const projectId = (payload as { projectId?: string }).projectId;
    for (const entry of this.#entries) {
      if (!projectId || projectId !== entry.projectId.value) continue;
      const handler = entry.handlers[event] as ((p: unknown) => void) | undefined;
      handler?.(payload);
    }
  }

  install(app: App): void {
    app.provide(REALTIME_KEY, this);
  }
}

export const REALTIME_KEY: InjectionKey<Realtime> = Symbol('dometry-realtime');

export function useRealtime(): Realtime {
  const client = inject(REALTIME_KEY, null);
  if (!client) throw new Error('useRealtime() called without the realtime plugin installed');
  return client;
}
